import { Copy, Settings2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import chatgptLogo from "@/src/common/assets/images/chatgpt.png";
import claudeLogo from "@/src/common/assets/images/claude.png";
import { Button } from "@/src/common/components/ui/button";
import { Card } from "@/src/common/components/ui/card";
import { toastService } from "@/src/common/services/toast.service";
import { McpSetupModal } from "@/src/settings/components/modals/mcp-setup.modal";

export const MCP_SERVER_URL = "https://mcp.excelcentermemphis.com/mcp";

type Provider = "claude" | "chatgpt";

interface ProviderCardProps {
	logo: string;
	titleKey: "settings.mcp.claude.title" | "settings.mcp.chatgpt.title";
	subtitleKey: "settings.mcp.claude.subtitle" | "settings.mcp.chatgpt.subtitle";
	ctaKey: "settings.mcp.claude.install" | "settings.mcp.chatgpt.install";
	onSetup: () => void;
}

function ProviderCard({ logo, titleKey, subtitleKey, ctaKey, onSetup }: ProviderCardProps) {
	const { t } = useTranslation("common");
	return (
		<Card className="flex flex-col gap-4 p-5">
			<div className="flex items-center gap-3">
				<img src={logo} alt="" className="size-10 rounded-s object-contain" />
				<div className="flex flex-col">
					<h3 className="text-sm font-semibold text-foreground">{t(titleKey)}</h3>
					<p className="text-xs text-muted-foreground">{t(subtitleKey)}</p>
				</div>
			</div>
			<Button variant="primary" onPress={onSetup} className="self-start">
				<Settings2 className="size-4" aria-hidden />
				{t(ctaKey)}
			</Button>
		</Card>
	);
}

export function McpSection() {
	const { t } = useTranslation("common");
	const [activeProvider, setActiveProvider] = useState<Provider | null>(null);

	const copyServerUrl = async () => {
		try {
			await navigator.clipboard.writeText(MCP_SERVER_URL);
			toastService.success(t("settings.mcp.copy_success"));
		} catch {
			toastService.error(t("settings.mcp.copy_error"));
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2 rounded-s border border-border bg-surface p-3">
				<span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t("settings.mcp.server_url_label")}</span>
				<div className="flex items-center gap-2">
					<code className="flex-1 truncate rounded-xs bg-card px-2 py-1.5 font-mono text-xs text-foreground">{MCP_SERVER_URL}</code>
					<Button variant="outline" size="sm" onPress={() => void copyServerUrl()}>
						<Copy className="size-3.5" aria-hidden />
						{t("settings.mcp.copy_url")}
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
				<ProviderCard
					logo={claudeLogo}
					titleKey="settings.mcp.claude.title"
					subtitleKey="settings.mcp.claude.subtitle"
					ctaKey="settings.mcp.claude.install"
					onSetup={() => setActiveProvider("claude")}
				/>
				<ProviderCard
					logo={chatgptLogo}
					titleKey="settings.mcp.chatgpt.title"
					subtitleKey="settings.mcp.chatgpt.subtitle"
					ctaKey="settings.mcp.chatgpt.install"
					onSetup={() => setActiveProvider("chatgpt")}
				/>
			</div>

			<McpSetupModal
				provider={activeProvider}
				onOpenChange={(open) => {
					if (!open) setActiveProvider(null);
				}}
				serverUrl={MCP_SERVER_URL}
				onCopy={copyServerUrl}
			/>
		</div>
	);
}
